import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

let registered = false

/**
 * 注册 GSAP 插件（幂等）
 */
export function setupGsap() {
  if (registered || typeof window === 'undefined') return gsap
  gsap.registerPlugin(ScrollTrigger)
  registered = true
  return gsap
}

/**
 * 解析 Vue ref / 原生节点
 * @param {Element|{ $el?: Element }|null|undefined} ref
 * @returns {Element|null}
 */
export function resolveEl(ref) {
  if (!ref) return null
  return ref.$el || ref
}

/**
 * 将目标统一转为元素数组
 * @param {any} targets
 * @returns {Element[]}
 */
function toEls(targets) {
  return gsap.utils.toArray(targets).filter(Boolean)
}

/**
 * 创建页面/组件级动效实例（带统一销毁）
 * @param {Element|string} [scope] gsap.context 作用域，传入组件根节点更安全
 * @example
 * // 组件内
 * created() { this.motion = this.$createMotion() }
 * mounted() {
 *   this.motion.reveal(this.$refs.cards, { stagger: 0.08 })
 *   this.motion.hoverLift(this.$refs.navLinks)
 * }
 * beforeDestroy() { this.motion.kill() }
 */
export function createMotion(scope) {
  setupGsap()

  const ctx = gsap.context(() => {}, scope || undefined)
  const hoverCleanups = []
  const timelines = []

  const trackTl = (tl) => {
    timelines.push(tl)
    return tl
  }

  const api = {
    gsap,
    ScrollTrigger,

    resolveEl,

    set(targets, vars) {
      return gsap.set(targets, vars)
    },

    to(targets, vars) {
      return gsap.to(targets, vars)
    },

    from(targets, vars) {
      return gsap.from(targets, vars)
    },

    fromTo(targets, fromVars, toVars) {
      return gsap.fromTo(targets, fromVars, toVars)
    },

    timeline(vars) {
      return trackTl(gsap.timeline(vars))
    },

    /**
     * 淡入上移（适合区块/卡片入场）
     */
    reveal(targets, options = {}) {
      const {
        y = 24,
        x = 0,
        duration = 0.5,
        stagger = 0.06,
        delay = 0,
        ease = 'power3.out',
        autoAlpha = 1,
      } = options
      const els = toEls(targets)
      if (!els.length) return null

      gsap.set(els, { autoAlpha: 0, y, x })
      return gsap.to(els, {
        autoAlpha,
        y: 0,
        x: 0,
        duration,
        stagger,
        delay,
        ease,
      })
    },

    /**
     * 按步骤编排入场
     * @param {Array<{ targets: any, at?: string|number, [key: string]: any }>} steps
     */
    sequence(steps = [], options = {}) {
      const { defaults = { ease: 'power3.out' } } = options
      const tl = trackTl(gsap.timeline({ defaults }))

      steps.forEach((step) => {
        if (!step || !step.targets) return
        const { targets, at = '>', ...vars } = step
        const els = toEls(targets)
        if (!els.length) return
        tl.to(els, vars, at)
      })

      return tl
    },

    /**
     * 通用悬停动效
     */
    hover(targets, options = {}) {
      const {
        enter = {},
        leave = {},
        duration = 0.25,
        ease = 'power2.out',
      } = options

      toEls(targets).forEach((el) => {
        const onEnter = () => {
          gsap.to(el, { ...enter, duration, ease, overwrite: 'auto' })
        }
        const onLeave = () => {
          gsap.to(el, { ...leave, duration, ease, overwrite: 'auto' })
        }
        el.addEventListener('mouseenter', onEnter)
        el.addEventListener('mouseleave', onLeave)
        hoverCleanups.push(() => {
          el.removeEventListener('mouseenter', onEnter)
          el.removeEventListener('mouseleave', onLeave)
        })
      })

      return api
    },

    /** 悬停上浮 + 透明度 */
    hoverLift(targets, options = {}) {
      const {
        y = -3,
        opacity = 0.72,
        duration = 0.28,
        ease = 'power2.out',
      } = options
      return api.hover(targets, {
        enter: { y, opacity },
        leave: { y: 0, opacity: 1 },
        duration,
        ease,
      })
    },

    /** 悬停缩放 */
    hoverScale(targets, options = {}) {
      const {
        scale = 1.04,
        duration = 0.25,
        ease = 'power2.out',
      } = options
      return api.hover(targets, {
        enter: { scale },
        leave: { scale: 1 },
        duration,
        ease,
      })
    },

    /** 悬停透明度 */
    hoverFade(targets, options = {}) {
      const {
        opacity = 0.75,
        duration = 0.22,
        ease = 'power2.out',
      } = options
      return api.hover(targets, {
        enter: { opacity },
        leave: { opacity: 1 },
        duration,
        ease,
      })
    },

    /**
     * 折叠面板开合（高度 + 子项 stagger）
     * @param {Element} el 容器
     * @param {boolean} open
     * @param {{ items?: any, duration?: number, itemDuration?: number, stagger?: number }} [options]
     */
    toggleCollapse(el, open, options = {}) {
      const node = resolveEl(el)
      if (!node) return null

      const {
        items,
        duration = 0.36,
        itemDuration = 0.28,
        stagger = 0.04,
        easeOpen = 'power3.out',
        easeClose = 'power2.inOut',
      } = options

      const itemEls = items ? toEls(items) : []

      // 清掉同容器上一次折叠动画
      gsap.killTweensOf(node)
      if (itemEls.length) gsap.killTweensOf(itemEls)

      if (open) {
        gsap.set(node, { autoAlpha: 1, height: 'auto', overflow: 'hidden' })
        const fullHeight = node.scrollHeight
        gsap.set(node, { height: 0 })
        if (itemEls.length) gsap.set(itemEls, { autoAlpha: 0, y: -8 })

        const tl = trackTl(gsap.timeline({ defaults: { ease: easeOpen } }))
        tl.to(node, { height: fullHeight, duration })
        if (itemEls.length) {
          tl.to(
            itemEls,
            {
              autoAlpha: 1,
              y: 0,
              duration: itemDuration,
              stagger,
            },
            `-=${duration * 0.5}`
          )
        }
        tl.add(() => {
          gsap.set(node, { height: 'auto', overflow: 'visible' })
        })
        return tl
      }

      gsap.set(node, { height: node.scrollHeight, overflow: 'hidden' })
      const tl = trackTl(gsap.timeline({ defaults: { ease: easeClose } }))
      if (itemEls.length) {
        tl.to(itemEls, {
          autoAlpha: 0,
          y: -6,
          duration: Math.min(0.16, itemDuration),
          stagger: Math.min(0.02, stagger),
        })
      }
      tl.to(
        node,
        {
          height: 0,
          autoAlpha: 0,
          duration: duration * 0.8,
        },
        itemEls.length ? '-=0.05' : 0
      )
      return tl
    },

    /**
     * 汉堡菜单图标 ↔ 关闭图标
     * @param {NodeList|Element[]} lines 三条线
     * @param {boolean} open
     */
    toggleMenuIcon(lines, open, options = {}) {
      const els = toEls(lines)
      if (els.length < 3) return null

      const {
        offset = 4.5,
        duration = 0.28,
        ease = 'power2.out',
      } = options

      if (open) {
        gsap.to(els[0], { y: offset, rotation: 45, duration, ease })
        gsap.to(els[1], { autoAlpha: 0, duration: duration * 0.65, ease })
        gsap.to(els[2], { y: -offset, rotation: -45, duration, ease })
      } else {
        gsap.to(els[0], { y: 0, rotation: 0, duration, ease })
        gsap.to(els[1], { autoAlpha: 1, duration: duration * 0.8, ease })
        gsap.to(els[2], { y: 0, rotation: 0, duration, ease })
      }
      return api
    },

    /**
     * 滚动触发入场（需 ScrollTrigger）
     */
    revealOnScroll(targets, options = {}) {
      const {
        y = 40,
        duration = 0.7,
        stagger = 0.08,
        start = 'top 85%',
        once = true,
        ease = 'power3.out',
      } = options
      const els = toEls(targets)
      if (!els.length) return null

      gsap.set(els, { autoAlpha: 0, y })
      return gsap.to(els, {
        autoAlpha: 1,
        y: 0,
        duration,
        stagger,
        ease,
        scrollTrigger: {
          trigger: options.trigger || els[0],
          start,
          once,
          ...(options.scrollTrigger || {}),
        },
      })
    },

    /**
     * 视差滚动（参考理想汽车官网风格）
     * @param {any} targets 背景层
     * @param {{ yPercent?: number, scale?: number, scrub?: boolean|number, trigger?: any }} [options]
     */
    parallax(targets, options = {}) {
      const {
        yPercent = -15,
        scale = 1.08,
        scrub = 1,
        start = 'top bottom',
        end = 'bottom top',
        ease = 'none',
      } = options
      const els = toEls(targets)
      if (!els.length) return null

      gsap.set(els, { scale, transformOrigin: 'center center' })
      return gsap.to(els, {
        yPercent,
        ease,
        scrollTrigger: {
          trigger: options.trigger || els[0].parentElement || els[0],
          start,
          end,
          scrub,
          ...(options.scrollTrigger || {}),
        },
      })
    },

    /**
     * 区块标题 + 内容错落入场
     */
    sectionReveal(sectionEl, options = {}) {
      const node = resolveEl(sectionEl)
      if (!node) return null

      const {
        titleSel = '.df-reveal-title',
        itemSel = '.df-reveal-item',
        y = 48,
        duration = 0.85,
        stagger = 0.1,
        start = 'top 78%',
      } = options

      const titles = node.querySelectorAll(titleSel)
      const items = node.querySelectorAll(itemSel)
      const all = [...titles, ...items]
      if (!all.length) return null

      gsap.set(all, { autoAlpha: 0, y })
      const tl = trackTl(
        gsap.timeline({
          scrollTrigger: {
            trigger: node,
            start,
            once: true,
          },
        })
      )
      if (titles.length) {
        tl.to(titles, { autoAlpha: 1, y: 0, duration, ease: 'power3.out', stagger: 0.08 })
      }
      if (items.length) {
        tl.to(
          items,
          { autoAlpha: 1, y: 0, duration: duration * 0.9, ease: 'power3.out', stagger },
          titles.length ? '-=0.45' : 0
        )
      }
      return tl
    },

    /** 销毁本实例创建的全部动效与悬停监听 */
    kill() {
      hoverCleanups.splice(0).forEach((fn) => {
        try {
          fn()
        } catch (e) {
          /* ignore */
        }
      })
      timelines.splice(0).forEach((tl) => {
        if (tl && tl.kill) tl.kill()
      })
      ctx.revert()
    },
  }

  return api
}

/**
 * Vue 插件：全局挂载 $gsap / $createMotion
 */
export function installGsap(Vue) {
  setupGsap()
  Vue.prototype.$gsap = gsap
  Vue.prototype.$ScrollTrigger = ScrollTrigger
  Vue.prototype.$createMotion = createMotion
  Vue.prototype.$resolveEl = resolveEl
}

export { gsap, ScrollTrigger }

export default {
  setupGsap,
  createMotion,
  installGsap,
  resolveEl,
  gsap,
  ScrollTrigger,
}
